import clsx from 'clsx';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import mergeClasses from '../../../utils/shallowMerge';
import InlineMessage from '../inlineMessage';
import defaultClasses from './RadioButton.module.scss';
import { useRadioContext } from './useRadioContext';

export const RadioButton = forwardRef((props, ref) => {
  const {
    value,
    id,
    ariaLabelledBy,
    required,
    readonly,
    field,
    disabled,
    name,
    helpText,
    innerHelpText,
    label,
    icon,
    icons,
    onlyText,
    defaultChecked,
    customClasses,
    additionalContent,
    inputProps,
    className,
  } = props;

  const {
    handleOnFocus,
    handleOnBlur,
    handleOnChange,
    handleKeyDown,
    radioState,
    error,
    errorMessage,
    variant,
  } = useRadioContext();

  const labelRef = useRef(null);
  const inputNodeRef = useRef(null);
  const styles = mergeClasses(defaultClasses, customClasses);
  const fieldName = name || (field && field.name);
  let checked = false;

  if (defaultChecked) {
    checked = defaultChecked;
  } else {
    checked = radioState
      ? value === radioState
      : field && field.value
      ? value === field.value
      : '';
  }

  useImperativeHandle(ref, () => ({
    itemInput: inputNodeRef.current,
    itemLabel: labelRef.current,
    focusInput: () => {
      if (inputNodeRef) {
        inputNodeRef.current.focus();
      }
    },
    focusLabel: () => {
      if (labelRef) {
        labelRef.current.focus();
      }
    },
  }));

  const showIcons = (images) => {
    return (
      images &&
      images.map((item, index) => {
        return (
          <span key={index} className={styles.item}>
            {item}
          </span>
        );
      })
    );
  };

  return (
    <div className={`${styles.rootWrapper} ${className}`}>
      <label
        ref={labelRef}
        role="radio"
        htmlFor={id}
        tabIndex={disabled ? undefined : 0}
        data-value={value}
        aria-checked={checked}
        aria-disabled={disabled}
        onClick={
          disabled
            ? undefined
            : (e) => {
                inputNodeRef && handleOnChange(e, inputNodeRef.current, value);
              }
        }
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                inputNodeRef && handleKeyDown(e, inputNodeRef.current, value);
              }
        }
        className={clsx(styles.root, {
          [` ${styles.error}`]: error,
          [` ${styles.boxed}`]: variant === 'boxed',
          [` ${styles.disabled}`]: disabled,
          [` ${styles.readonly}`]: readonly,
        })}
      >
        <div className={styles.mainContainer}>
          <div className={styles.inputBlock}>
            <input
              id={id}
              ref={(inputRef) => {
                inputNodeRef.current = inputRef;
                ref instanceof Function ? ref(inputRef) : inputRef;
              }}
              type="radio"
              aria-labelledby={ariaLabelledBy}
              name={fieldName}
              defaultChecked={checked}
              checked={checked}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              disabled={disabled}
              readOnly={readonly}
              required={required}
              value={value}
              className={clsx('', {
                [styles.inputLabel]: onlyText,
                [styles.inputRadio]: !onlyText,
                [styles.isChecked]: checked,
              })}
              {...inputProps}
            />
            {!onlyText && <div className={styles.radioIcon} />}
            <div className={onlyText ? styles.labelText : styles.labelRadio}>
              {label && label}
              {innerHelpText && (
                <div className={styles.descriptionContainer}>
                  {innerHelpText}
                </div>
              )}
            </div>
            {helpText && (
              <div className={styles.descriptionContainer}>{helpText}</div>
            )}
          </div>
          <div className={`${styles.content} ${styles.positionIcon}`}>
            {icon && <span className={styles.icon}>{icon}</span>}
            {icons && showIcons(icons)}
          </div>
        </div>
        {additionalContent && (
          <div className={styles.additionalContent}>{additionalContent}</div>
        )}
      </label>
      {error && errorMessage ? (
        <InlineMessage type="error" message={errorMessage} />
      ) : null}
    </div>
  );
});
